import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MdClose, MdUpload } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/shared/Toast';

interface EligibleOrder {
  _id: string;
  poNumber: string;
  vendorOrganizationId?: { name?: string };
  rfqId?: { rfqNumber?: string; vesselName?: string };
  items: Array<{
    itemIndex: number;
    itemDescription: string;
    partNo?: string;
    quantity: number;
    uom: string;
    alreadyClaimed?: boolean;
  }>;
}

export function RaiseClaimModal({ onClose }: { onClose: () => void }) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedPoId, setSelectedPoId] = useState('');
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const { data: eligibleOrders = [], isLoading } = useQuery<EligibleOrder[]>({
    queryKey: ['admin-claim-eligible-orders'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/v1/admin/claims/eligible-orders');
      if (!res.ok) throw new Error('Failed to load eligible orders');
      const json = await res.json();
      return json.data || [];
    },
  });

  const selectedOrder = useMemo(
    () => eligibleOrders.find((o) => o._id === selectedPoId),
    [eligibleOrders, selectedPoId]
  );

  const claimableItems = useMemo(
    () => (selectedOrder?.items || []).filter((item) => !item.alreadyClaimed),
    [selectedOrder]
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append('purchaseOrderId', selectedPoId);
      formData.append('reason', reason.trim());
      formData.append('itemIndices', JSON.stringify(selectedIndices));
      files.forEach((file) => formData.append('documents', file));

      const res = await authenticatedFetch('/api/v1/admin/claims', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to raise claim');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-delivery-claims'] });
      queryClient.invalidateQueries({ queryKey: ['admin-claim-eligible-orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pipeline-overview'] });
      showToast('Claim created successfully', 'success');
      onClose();
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const canSubmit =
    !!selectedPoId && selectedIndices.length > 0 && reason.trim().length > 0 && !createMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-[#0088cc]/15 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Create Claim</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
              Select a delivered purchase order, items, and describe the issue.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-[#0088cc]/10">
            <MdClose className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
              Purchase Order *
            </label>
            {isLoading ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading eligible orders…</p>
            ) : eligibleOrders.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-4">
                No delivered orders are available for claims right now.
              </p>
            ) : (
              <select
                value={selectedPoId}
                onChange={(e) => {
                  setSelectedPoId(e.target.value);
                  setSelectedIndices([]);
                }}
                className="w-full rounded-xl border border-[#0088cc]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0088cc]"
              >
                <option value="">Select a purchase order</option>
                {eligibleOrders.map((order) => (
                  <option key={order._id} value={order._id}>
                    {order.poNumber}
                    {order.rfqId?.rfqNumber ? ` · RFQ ${order.rfqId.rfqNumber}` : ''}
                    {order.rfqId?.vesselName ? ` · ${order.rfqId.vesselName}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {claimableItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  Items to Claim *
                </label>
                <button
                  type="button"
                  onClick={() => setSelectedIndices(claimableItems.map((item) => item.itemIndex))}
                  className="text-xs font-bold text-[#0088cc] hover:underline"
                >
                  Select all
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-[#0088cc]/15 rounded-xl p-3">
                {claimableItems.map((item) => (
                  <label
                    key={item.itemIndex}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                      selectedIndices.includes(item.itemIndex)
                        ? 'border-[#0088cc] bg-blue-50/50'
                        : 'border-[#0088cc]/15 hover:bg-[#0088cc]/5'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIndices.includes(item.itemIndex)}
                      onChange={() =>
                        setSelectedIndices((prev) =>
                          prev.includes(item.itemIndex)
                            ? prev.filter((i) => i !== item.itemIndex)
                            : [...prev, item.itemIndex]
                        )
                      }
                      className="mt-1"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{item.itemDescription}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {item.partNo ? `Part ${item.partNo} · ` : ''}
                        Qty {item.quantity} {item.uom}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
              Reason for Claim *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Describe the damage, missing items, or quality issue…"
              className="w-full rounded-xl border border-[#0088cc]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0088cc]"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
              Photos / Documents (optional)
            </label>
            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#0088cc]/20 p-6 cursor-pointer hover:border-[#0088cc]">
              <MdUpload className="w-6 h-6 text-[#0088cc]/65" />
              <span className="text-sm text-[hsl(var(--muted-foreground))]">Upload images, PDF, or Word files</span>
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </label>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((file) => (
                  <li key={file.name} className="text-xs text-[hsl(var(--muted-foreground))]">
                    {file.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-[#0088cc]/15 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-[#0088cc]/20 text-sm font-semibold hover:bg-[#0088cc]/5"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => createMutation.mutate()}
            className="px-5 py-2.5 rounded-xl bg-[#0088cc] text-white text-sm font-bold hover:bg-[#0077b3] disabled:opacity-50"
          >
            {createMutation.isPending ? 'Submitting…' : 'Submit Claim'}
          </button>
        </div>
      </div>
    </div>
  );
}
